import { useState } from 'react';
import { Shield, Eye, EyeOff, Loader2, Fingerprint } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { authApi } from '../services/api';
import toast from 'react-hot-toast';
import logoImg from '../assets/logo.png';
import { APP_VERSION_FULL } from '../version';

export default function LoginPage() {
  const [form, setForm] = useState({ username: '', password: '' });
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate  = useNavigate();
  const location  = useLocation();
  const from      = (location.state as any)?.from?.pathname;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.username || !form.password) { 
      toast.error('Vui lòng nhập đủ thông tin', { style: { background: '#10101a', color: '#fff', border: '1px solid rgba(255,255,255,0.1)' }}); 
      return; 
    }
    setLoading(true);
    try {
      const res = await authApi.login(form.username, form.password);
      localStorage.setItem('access_token', res.accessToken);
      localStorage.setItem('refresh_token', res.refreshToken);
      toast.success('Xác thực thành công', { style: { background: '#10101a', color: '#fff', border: '1px solid rgba(255,255,255,0.1)' }});
      const portalType  = import.meta.env.VITE_PORTAL_TYPE || 'admin';
      const defaultPath = portalType === 'admin' ? '/dashboard' : portalType === 'agent' ? '/agent/dashboard' : '/portal/history';
      navigate(from ?? defaultPath, { replace: true });
    } catch {
      toast.error('Giao thức xác thực thất bại. Vui lòng thử lại.', { style: { background: '#10101a', color: '#fff', border: '1px solid rgba(255,255,255,0.1)' }});
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden bg-slate-50 dark:bg-[#050508] transition-colors duration-500">
      {/* Ambient background glows */}
      <div className="absolute top-1/4 left-1/4 w-[500px] h-[500px] bg-indigo-500/10 dark:bg-indigo-600/20 rounded-full blur-[120px] pointer-events-none animate-pulse" />
      <div className="absolute bottom-1/4 right-1/4 w-[500px] h-[500px] bg-purple-500/10 dark:bg-purple-600/20 rounded-full blur-[120px] pointer-events-none animate-pulse" style={{ animationDelay: '2s' }} />
      <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/stardust.png')] opacity-[0.03] dark:opacity-10 pointer-events-none" />

      <div className="w-full max-w-md relative z-10 transition-all duration-700">
        <div className="text-center mb-10">
          <div className="relative inline-flex items-center justify-center w-24 h-24 mb-6 group">
            <div className="absolute inset-x-0 bottom-0 top-0 bg-indigo-500/30 rounded-3xl blur-2xl opacity-50 group-hover:opacity-100 transition-opacity duration-500" />
            <div className="relative w-full h-full bg-white dark:bg-black/40 rounded-3xl border border-white/20 flex flex-col items-center justify-center shadow-2xl transform group-hover:scale-105 transition-transform duration-500 p-4">
              <img src={logoImg} alt="xDataAudit Logo" className="w-full h-full object-contain" />
            </div>
          </div>
          <h1 className="text-4xl font-heading font-black text-slate-900 dark:text-white tracking-widest drop-shadow-sm dark:drop-shadow-[0_0_15px_rgba(99,102,241,0.5)] leading-none">
            xDataAudit
          </h1>
          <p className="text-indigo-600 dark:text-indigo-400 font-black text-[11px] tracking-[0.2em] mt-4 flex items-center justify-center gap-2">
            <span className="w-8 h-px bg-indigo-500/20" />
            National Compliance Protocol
            <span className="w-8 h-px bg-indigo-500/20" />
          </p>
        </div>

        <div className="glass-panel p-10 rounded-[2.5rem] relative overflow-hidden border-slate-200 dark:border-white/5 bg-white/80 dark:bg-white/[0.02] shadow-2xl backdrop-blur-3xl">
          <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-indigo-500 via-purple-500 to-cyan-500 shadow-sm" />
          
          <div className="flex items-center gap-3 mb-10">
            <div className="p-2.5 bg-indigo-500/10 rounded-xl">
               <Fingerprint className="text-indigo-600 dark:text-indigo-400" size={24} />
            </div>
            <div>
              <h2 className="text-sm font-heading font-black text-slate-900 dark:text-white tracking-[0.1em]">Cổng xác thực</h2>
              <p className="text-[10px] font-black text-indigo-500/60 tracking-wider mt-0.5">Secure Gateway Access</p>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="group">
              <label className="block text-[10px] font-black text-slate-400 dark:text-slate-500 tracking-[0.1em] mb-3 ml-1">Tài khoản định danh</label>
              <div className="relative">
                <input
                  type="text" 
                  value={form.username}
                  onChange={e => setForm(f => ({ ...f, username: e.target.value }))}
                  placeholder="CCCD hoặc Mã nhân viên"
                  className="w-full bg-slate-50 dark:bg-black/40 border border-slate-200 dark:border-white/10 text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-600 rounded-2xl px-6 py-4 text-sm outline-none focus:border-indigo-500 transition-all font-bold shadow-inner"
                />
              </div>
            </div>
            
            <div className="group">
              <label className="block text-[10px] font-black text-slate-400 dark:text-slate-500 tracking-[0.1em] mb-3 ml-1">Mã bảo mật (OTP/PWD)</label>
              <div className="relative">
                <input
                  type={showPwd ? 'text' : 'password'} 
                  value={form.password}
                  onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                  placeholder="••••••••••••"
                  className="w-full bg-slate-50 dark:bg-black/40 border border-slate-200 dark:border-white/10 text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-600 rounded-2xl px-6 py-4 pr-14 text-sm outline-none focus:border-indigo-500 transition-all font-mono shadow-inner"
                />
                <button 
                  type="button" 
                  onClick={() => setShowPwd(s => !s)}
                  className="absolute right-5 top-1/2 -translate-y-1/2 text-slate-300 dark:text-slate-600 hover:text-indigo-500 dark:hover:text-indigo-400 transition-all p-1"
                >
                  {showPwd ? <EyeOff size={20}/> : <Eye size={20}/>}
                </button>
              </div>
            </div>

            <button 
              type="submit" 
              disabled={loading}
              className="w-full relative group overflow-hidden bg-gradient-to-r from-indigo-600 to-indigo-700 hover:from-indigo-500 hover:to-indigo-600 text-white rounded-2xl py-4 mt-6 text-[11px] font-black tracking-[0.2em] transition-all shadow-xl shadow-indigo-500/20 active:scale-95 disabled:opacity-70"
            >
              <div className="flex items-center justify-center gap-3 relative z-10">
                {loading ? <Loader2 size={18} className="animate-spin" /> : <Shield size={18} />}
                <span>{loading ? 'Đang thiết lập kết nối...' : 'Xác thực định danh'}</span>
              </div>
            </button>
          </form>
          
          <div className="mt-10 pt-8 border-t border-slate-200 dark:border-white/5 text-center">
            <p className="text-[10px] text-slate-500 dark:text-slate-500 tracking-wider font-black">
              Hệ thống thẩm định dữ liệu quốc gia <br/>
              <span className="text-indigo-600 dark:text-indigo-400/60 mt-2 block">C12 - Bộ Công an - 2026</span>
            </p>
          </div>
        </div>

        <div className="mt-8 text-center text-[10px] font-black text-slate-500 dark:text-slate-500 tracking-wider leading-loose">
           <p>© Bản quyền thuộc Trung tâm Dữ liệu Quốc gia</p>
           <p className="mt-1 opacity-100 dark:opacity-50">Phiên bản {APP_VERSION_FULL}</p>
        </div>
      </div>
    </div>
  );
}
